import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { publicApi } from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { markCameFromApp } from '../../lib/appHandoff'
import { usePageMeta } from '../../hooks/usePageMeta'

const FLOWS = ['one-time', 'recurring', 'roundup']

// Handoff token -> promise of the redeem outcome (true = signed in). Module-level
// so React StrictMode's double effect (and remounts) redeem each token once and
// every mount awaits the same result.
const redemptions = new Map()

function redeemOnce(h) {
  if (!redemptions.has(h)) {
    redemptions.set(
      h,
      (async () => {
        try {
          const { tokenHash, email } = await publicApi.redeemWebHandoff(h)
          const { data } = await supabase.auth.getSession()
          const current = data?.session
          const sameAccount =
            current?.user?.email?.toLowerCase() === String(email).toLowerCase()
          if (!sameAccount) {
            if (current) await supabase.auth.signOut({ scope: 'local' })
            const { error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: 'magiclink',
            })
            if (error) throw error
          }
          return true
        } catch {
          return false
        }
      })(),
    )
  }
  return redemptions.get(h)
}

export default function Give() {
  const { parishId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  usePageMeta({ title: 'Give' })

  const requestedFlow = searchParams.get('flow')
  const flow = FLOWS.includes(requestedFlow) ? requestedFlow : 'one-time'
  const h = searchParams.get('h')
  const [showPrompt, setShowPrompt] = useState(false)

  const givePath = `/give/${parishId}?flow=${flow}`

  useEffect(() => {
    markCameFromApp()

    const toSignIn = () =>
      navigate(`/sign-in?redirect=${encodeURIComponent(givePath)}`, { replace: true })

    const route = async () => {
      if (flow === 'one-time') {
        navigate(`/parishes/${parishId}`, { replace: true })
        return
      }
      const { data } = await supabase.auth.getSession()
      if (!data?.session) {
        setShowPrompt(true)
        return
      }
      navigate(
        flow === 'recurring'
          ? `/app/donor/recurring-donation-setup?parishId=${parishId}`
          : `/app/donor/roundups?parishId=${parishId}`,
        { replace: true },
      )
    }

    const run = async () => {
      try {
        if (h) {
          window.history.replaceState(window.history.state, '', givePath)
          const ok = await redeemOnce(h)
          if (!ok) {
            // One-time giving allows guests; the other flows need an account.
            if (flow === 'one-time') {
              navigate(`/parishes/${parishId}`, { replace: true })
            } else {
              toSignIn()
            }
            return
          }
        }
        await route()
      } catch {
        toSignIn()
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (showPrompt) {
    return (
      <div className="max-w-md mx-auto py-24 px-4 text-center">
        <h1 className="text-2xl font-bold font-display text-charcoal mb-2">
          {flow === 'recurring'
            ? 'Sign in to set up recurring giving'
            : 'Sign in to set up round-ups'}
        </h1>
        <p className="text-charcoal/70 mb-6">
          Recurring gifts and round-ups are linked to your Claritas account.
        </p>
        <Link
          to={`/sign-in?redirect=${encodeURIComponent(givePath)}`}
          className="inline-block bg-gold text-white font-semibold px-6 py-3 rounded-lg"
        >
          Sign in
        </Link>
        <div className="mt-4">
          <Link to={`/parishes/${parishId}`} className="text-gold font-semibold">
            Give once as a guest instead
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 text-gold animate-spin" />
    </div>
  )
}
